import type {AxiosResponse} from 'axios'
import Axios from 'axios'
import * as QueryString from 'query-string'
import * as R from 'ramda'
import type {
  TAnyEndpoint,
  THTTPVerb,
  TPathPiecesOf,
  TRequestOf,
  TResponseOf,
} from '@kapitalist/contract/endpoints/types'
import type {TFormError} from '@kapitalist/contract/types'
import type {EmptyObject} from '@kapitalist/utils/EmptyObject'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import {convertNullsToUndefined} from '@kapitalist/utils/falsiness'
import {buildGetApiPromisePoller} from '~/api/APIPoller/Get'
import {buildPostApiPromisePoller} from '~/api/APIPoller/Post'
import {buildGetApiPromiseManager} from '~/api/ApiPromiseManager/Get'
import {buildPostApiPromiseManager} from '~/api/ApiPromiseManager/Post'
import APILoggedOutError from '~/api/client/APIError/APILoggedOutError'
import type APIError from '~/api/client/APIError/base'
import {
  buildFromAxiosError,
  constructMockRequestFailure,
  constructUnreachableServerError,
} from '~/api/client/APIError/builder'
import type {IAxiosHTTPClient} from '~/api/client/axios'
import {axiosHTTPClient} from '~/api/client/axios'
import {apiDispatch} from '~/api/client/dispatch'
import type {
  IJSONObject,
  IJSONObjectConvertible,
  TGenericizedPathWithHTTPVerb,
} from '~/api/client/types'
import {backendHosts} from '~/api/client/types'
import MockConfig from '~/api/mocks/config'
import environment, {inTesting} from '~/config/environment'
import {loggedOutRemotely, restartSessionTimer} from '~/redux/reducers/auth'
import {ordinalSuffixOf} from '~/utils/language'

/**
 * Strings only: this type is shared with the backend, which reads query params back as
 * strings, so a `{page: number}` endpoint would type its handler `number` while receiving
 * `'2'`. Arrays are worse — `{tags: ['a']}` comes back as the bare string `'a'`. Declare
 * params as strings and parse at the point of use.
 */
export type TGetQueryParameters = {[key in string]?: string}

export interface IApiPathPieces {
  [k: string]: string | undefined
}

const DEFAULT_MOCK_DELAY = 500

// This is an abstract and never exported class
// Use the GetApi and PostApi classes below
abstract class BaseApiClient<
  PostRequestParamsType extends IJSONObjectConvertible | undefined,
  GetRequestParamsType extends TGetQueryParameters | undefined,
  ResponseType extends IJSONObject,
  PathPiecesType extends IApiPathPieces = EmptyObject,
  ResultType = ResponseType,
  UnprocessedPostRequestParamsType = PostRequestParamsType,
> {
  httpClient: IAxiosHTTPClient

  constructor() {
    const backendSource = environment.backendSource
    this.httpClient = axiosHTTPClient(
      backendSource === 'mock' ? '' : backendHosts[backendSource]
    )
  }

  // private backing for the lazily-initialized genericizedPathWithHTTPVerb getter below
  // (only really done this fancy way because we can't access the abstract instance property
  // `getPath` in `constructor` to compute this once, not because the initialization w/ Proxy
  // is known to be expensive or anything)
  private _genericizedPathWithHTTPVerb: TGenericizedPathWithHTTPVerb | undefined =
    undefined

  /**
   * This stores a genericized version of the path that this API hits on the backend,
   * prefixed by the HTTP verb.
   *
   * Genericized meaning all UUIDs or other dynamic path pieces are replaced with a
   * placeholder like `:playerId` or `:fooId`.
   * e.g. `POST /players/:playerId/foo/bar`
   *
   * This is useful for error reporting or other things that want to know what API was
   * hit, regardless of which org/user/etc was hitting it.
   *
   *
   * Docs on the error reporting use case:
   *
   * Common wrappers of the BaseApiClient will report certain failures for you.
   * This method generates a default groupingHash for those error reports to use, so that all errors from
   * the same API are grouped together, regardless of their call-site.
   *
   * For example, {@link PostApiPromiseManager}, {@link GetApiPromiseManager}.
   */
  public get genericizedPathWithHTTPVerb(): TGenericizedPathWithHTTPVerb {
    return (this._genericizedPathWithHTTPVerb ??= (() => {
      return `${this.getHTTPVerb()} ${this.getPath(
        new Proxy({} as PathPiecesType, {
          get: (_target, property) => `:${String(property)}`,
        })
      )}`
    })())
  }

  protected abstract getHTTPVerb(): THTTPVerb
  protected abstract getPath(pathPieces: PathPiecesType): string

  /**
   * Returns a mocked response, optionally configured based on the request if your mock is fancy like that.
   * @param {PostRequestParamsType | GetRequestParamsType} params Your mock response can vary based on the request body
   * @param {PathPiecesType} pathPieces Your mock response may also vary based on params passed in the URL
   * @returns {ResponseType}
   */
  protected abstract getMockResponse(
    params: PostRequestParamsType | GetRequestParamsType | undefined,
    pathPieces: PathPiecesType
  ): ResponseType

  /**
   * Override this in a subclass if you'd like to be able to pre-process the params before they are sent to the server.
   * @param {UnprocessedPostRequestParamsType} params
   * @returns {PostRequestParamsType} The processed params. The default implementation doesn't modify them.
   */
  protected preProcessPostParams(
    params: UnprocessedPostRequestParamsType | undefined
  ): PostRequestParamsType | undefined {
    // have to force a conversion here. Otherwise one would have to overwrite
    // it on every simple API. Bit unfortunate since this conversion
    // is not type safe. But I think it's a reasonable compromise
    return params as PostRequestParamsType
  }

  /**
   * Converts the server response to a type more convenient to your consumers
   * @param {ResponseType} The response type from the server
   * @returns {ResultType} The result type for the apis consumers
   */
  protected abstract transformResponse(
    response: ResponseType,
    params: PostRequestParamsType | GetRequestParamsType | undefined,
    pathPieces: PathPiecesType
  ): ResultType

  /**
   * Whether or not a successful server response should reset the session timer
   * that tracks how much longer the user is logged in.
   * Override this in a subclass for an API that should not reset the session timer.
   */
  protected successShouldResetSessionTimer(_result: ResultType): boolean {
    return true
  }

  /**
   * Returns whether or not the API should automatically retry given the error received & number of previous attempts.
   * Default: no retries.
   * Override this in a subclass for an API that wants retries.
   */
  protected automaticallyRetry(_error: APIError, _failedAttempts: number): boolean {
    return false
  }

  /**
   * Override this in a subclass if you'd like to be have mocks return failures sometimes.
   * @param {PostRequestParamsType | GetRequestParamsType} params
   * @param {PathPiecesType} pathPieces
   * @returns {TFormError | null} The error object, if the mock should fail, or `null`
   *  if it should succeed. The default implementation returns `null` (no errors)
   */
  protected mockRequestFailure(
    _params: PostRequestParamsType | GetRequestParamsType | undefined,
    _pathPieces: PathPiecesType
  ): TFormError | null {
    return null
  }

  protected call = (
    getParams: GetRequestParamsType | undefined,
    postParams: UnprocessedPostRequestParamsType | undefined,
    pathPieces: PathPiecesType,
    signal?: AbortSignal
  ): Promise<ResultType> => {
    const processedParams: PostRequestParamsType | undefined =
      this.preProcessPostParams(postParams)
    const callPromise = () => {
      if (MockConfig.enabled) {
        return this.callMock(getParams, processedParams, pathPieces, signal)
      } else {
        return this.callServer(getParams, processedParams, pathPieces, signal)
      }
    }

    const promiseSuccessHandler = (r: ResultType): ResultType => {
      if (this.successShouldResetSessionTimer(r)) {
        apiDispatch(restartSessionTimer())
      }

      return r
    }

    // adapted from https://tech.mybuilder.com/handling-retries-and-back-off-attempts-with-javascript-promises/
    const retry = <T>(
      attemptsMadeSoFar: number,
      promiseFn: () => Promise<T>
    ): Promise<T> =>
      promiseFn().catch(e => {
        if (this.automaticallyRetry(e, attemptsMadeSoFar)) {
          console.warn(
            `API failure. Retrying automatically... (${attemptsMadeSoFar}${ordinalSuffixOf(
              attemptsMadeSoFar
            )} retry)`,
            e
          )
          return retry(attemptsMadeSoFar + 1, promiseFn)
        } else {
          return Promise.reject(e)
        }
      })

    return retry(1, callPromise).then(promiseSuccessHandler)
  }

  protected mockDelay() {
    if (MockConfig.globalDelayMs) {
      return MockConfig.globalDelayMs
    }
    return DEFAULT_MOCK_DELAY
  }

  private callMock = (
    getParams: GetRequestParamsType | undefined,
    postParams: PostRequestParamsType | undefined,
    pathPieces: PathPiecesType,
    signal: AbortSignal | undefined
  ): Promise<ResultType> => {
    return new Promise<ResultType>((resolve, reject) => {
      console.log(
        `Hitting Mock Endpoint: ${this.constructor.name}`,
        getParams,
        postParams,
        pathPieces
      )
      if (this.mocksRequireLogin() && MockConfig.loggedOut) {
        console.warn('mocks: server logging you out.')
        reject(
          new APILoggedOutError({
            kind: 'loggedOut',
            statusCode: 401,
            statusText: 'Unauthorized',
            rawResponseData: {errors: {loggedOut: ['true']}},
            genericizedPathWithHTTPVerb: this.genericizedPathWithHTTPVerb,
          })
        )
      } else {
        setTimeout(
          () => {
            // If it's aborted then we don't want to resolve the mock promise,
            // which is what also happens in the non mock case
            if (!signal?.aborted) {
              const optionalFailure = this.mockRequestFailure(
                getParams || postParams,
                pathPieces
              )

              if (optionalFailure) {
                console.log(`Mock Failure: ${this.constructor.name}`)
                reject(constructMockRequestFailure(optionalFailure))
              } else {
                console.log(`Mock 200: ${this.constructor.name}`)
                resolve(
                  this.transformResponse(
                    this.getMockResponse(getParams ?? postParams, pathPieces),
                    getParams ?? postParams,
                    pathPieces
                  )
                )
              }
            }
          },
          inTesting ? 0 : this.mockDelay()
        )
      }
    })
  }

  private getPathWithParams = (
    pathPieces: PathPiecesType,
    getParams: GetRequestParamsType | undefined
  ) => {
    let path = this.getPath(pathPieces)
    if (!!getParams && !R.isEmpty(getParams)) {
      path = `${path}?${QueryString.stringify(getParams)}`
    }
    return path
  }

  private callServer = (
    getParams: GetRequestParamsType | undefined,
    postParams: PostRequestParamsType | undefined,
    pathPieces: PathPiecesType,
    signal?: AbortSignal
  ) => {
    const path = this.getPathWithParams(pathPieces, getParams)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const defaultedPostParams: IJSONObjectConvertible = postParams! || {}

    const call = (() => {
      const verb = this.getHTTPVerb()
      switch (verb) {
        case 'GET':
          return this.httpClient.get(path, signal)
        case 'POST':
          return this.httpClient.post(path, defaultedPostParams, signal)
        case 'QUERY':
          return this.httpClient.query(path, defaultedPostParams, signal)
        default:
          return unreachableCase(verb)
      }
    })()

    return new Promise<ResultType>((resolve, reject) => {
      call
        .then(r =>
          resolve(
            this.transformResponse(
              this.processResponse(r),
              getParams ?? postParams,
              pathPieces
            )
          )
        )
        .catch((e: unknown) => {
          // cancelled promises don't call `reject`, they just never return.
          if (!Axios.isCancel(e)) {
            console.warn('Error getting result from api', e)

            const apiError = Axios.isAxiosError(e)
              ? buildFromAxiosError(e, this.genericizedPathWithHTTPVerb)
              : constructUnreachableServerError()
            if (apiError instanceof APILoggedOutError) {
              apiDispatch(loggedOutRemotely())
            }
            reject(apiError)
          }
        })
    })
  }

  private processResponse(result: AxiosResponse<ResponseType>): ResponseType {
    // we've decided to convert all JSON `null`s to `undefined`s in the client
    // so we can continue to use the `?:` syntax for optional fields.
    // we also assume that nil bodies are `{}` because the FE uses `EmptyObject`
    // and generally assumes/requires that bodies are JSON objects.
    return convertNullsToUndefined(result.data || {})
  }

  // may be overriden if the specific API call requires login
  public mocksRequireLogin() {
    return false
  }
}

// The positional-generic base beneath `GetApi`. Clients extend `GetApi` instead; this is
// named directly only by the promise manager/poller builders below.
export abstract class GetApiBase<
  RequestParamsType extends TGetQueryParameters,
  ResponseType extends IJSONObject,
  PathPiecesType extends IApiPathPieces = EmptyObject,
  ResultType = ResponseType,
> extends BaseApiClient<
  undefined,
  RequestParamsType,
  ResponseType,
  PathPiecesType,
  ResultType,
  undefined
> {
  // no support for pre-processing get params right now
  PromiseManager = buildGetApiPromiseManager<
    RequestParamsType,
    ResponseType,
    PathPiecesType,
    ResultType
  >(this) // `this` is the concrete subclass

  // no support for pre-processing get params right now
  PromisePoller = buildGetApiPromisePoller<
    RequestParamsType,
    ResponseType,
    PathPiecesType,
    ResultType
  >(this) // `this` is the concrete subclass

  protected getHTTPVerb(): THTTPVerb {
    return 'GET'
  }
  // if you are looking for the promise manager we dont support that for GET
  // instead just use POST. We have opted to use POST in almost every place

  /**
   * Calls the API.
   * @param {RequestParamsType} params
   * @param {PathPiecesType} pathPieces
   * @param {AbortSignal} signal An optional `AbortSignal`; using one allows the
   * caller to cancel the promise before it returns (i.e.: if your React component unmounts before it returns)
   * @returns {Promise<ResponseType extends IJSONObject>}
   */
  execute(
    params: RequestParamsType,
    pathPieces: PathPiecesType,
    signal?: AbortSignal
  ): Promise<ResultType> {
    return this.call(params, undefined, pathPieces, signal)
  }
}

// The positional-generic base beneath `PostApi`. Clients extend `PostApi` instead; this is
// named directly only by the promise manager/poller builders below.
export abstract class PostApiBase<
  RequestParamsType extends IJSONObjectConvertible,
  ResponseType extends IJSONObject,
  PathPiecesType extends IApiPathPieces = EmptyObject,
  ResultType = ResponseType,
  UnprocessedRequestParamsType = RequestParamsType,
> extends BaseApiClient<
  RequestParamsType,
  undefined,
  ResponseType,
  PathPiecesType,
  ResultType,
  UnprocessedRequestParamsType
> {
  /** use this whenever we have a loading screen waiting for an API
   * as it properly does the cancellation of the promise
   */
  PromiseManager = buildPostApiPromiseManager<
    RequestParamsType,
    ResponseType,
    PathPiecesType,
    ResultType,
    UnprocessedRequestParamsType
  >(this) // `this` is the concrete subclass

  /** use this whenever we have a loading screen waiting for an API
   * as it properly does the cancellation of the promise
   */
  PromisePoller = buildPostApiPromisePoller<
    RequestParamsType,
    ResponseType,
    PathPiecesType,
    ResultType,
    UnprocessedRequestParamsType
  >(this) // `this` is the concrete subclass

  protected getHTTPVerb(): THTTPVerb {
    return 'POST'
  }

  /**
   * Calls the API.
   * @param {UnprocessedRequestParamsType} params
   * @param {PathPiecesType} pathPieces
   * @param {AbortSignal} signal An optional `AbortSignal`; using one allows the
   * caller to cancel the promise before it returns (i.e.: if your React component unmounts before it returns)
   * @returns {Promise<ResponseType extends IJSONObject>}
   */
  execute(
    params: UnprocessedRequestParamsType,
    pathPieces: PathPiecesType,
    signal?: AbortSignal
  ): Promise<ResultType> {
    return this.call(undefined, params, pathPieces, signal)
  }
}

/**
 * A POST client whose request, response, and path come from a `@kapitalist/contract` endpoint
 * entry. Subclasses name the entry twice — once as the type argument, once to `super()` —
 * because a value's type isn't reachable from an `extends` clause:
 *
 *     const endpoint = endpoints.pregame.joinGame
 *     export default class JoinGame extends PostApi<typeof endpoint>() {
 *       constructor() { super(endpoint) }
 *     }
 *
 * This is a *function returning a class* (note the `()` in `PostApi<…>()`) rather than a
 * plain generic class, because `extends` can't take type arguments that a subclass supplies:
 * `class JoinGame extends PostApi<typeof endpoint>` is a syntax error, and a generic base
 * would have to be instantiated at the point it's declared, not by each subclass. Calling a
 * generic function gets us a class already specialized to one endpoint, which is what lets
 * every member below be typed in terms of that endpoint. The pattern is sometimes called a
 * "class factory" or mixin.
 *
 * Note where the "body must be JSON-serializable" check lives. It is the `request?:` clause
 * of the `TEndpointType` constraint below, and it has to be: the base class is instantiated
 * with `TRequestOf<TEndpointType> & IJSONObjectConvertible`, and an intersection *merges*
 * rather than rejects. Given a body declaring `{onDone: () => void}`, that intersection
 * happily produces a type with a callable `onDone` instead of erroring, so the client
 * compiles and `JSON.stringify` silently drops the field at runtime.
 */
export const PostApi = <
  TEndpointType extends TAnyEndpoint & {request?: IJSONObjectConvertible},
  TResult = TResponseOf<TEndpointType>,
  // `IApiPathPieces` is the constraint, deliberately not intersected into the default: its
  // index signature would make every string key legal, so a misspelled piece would pass
  // excess-property checking.
  TPathPieces extends IApiPathPieces = TPathPiecesOf<TEndpointType>,
  TUnprocessedRequest = TRequestOf<TEndpointType>,
>() => {
  abstract class EndpointPostApi extends PostApiBase<
    TRequestOf<TEndpointType> & IJSONObjectConvertible,
    TResponseOf<TEndpointType> & IJSONObject,
    TPathPieces,
    TResult,
    TUnprocessedRequest
  > {
    constructor(protected readonly endpoint: TEndpointType) {
      super()
    }

    // Can be overridden.
    protected getPath(pathPieces: TPathPieces): string {
      // this `as` is correct when `TPathPieces` matches the endpoint.
      // overriding implementations will have to translate from TPathPieces to
      // `TPathPiecesOf<TEndpointType>` manually (and only override to do exactly that).
      return this.endpoint.path(pathPieces as TPathPiecesOf<TEndpointType>)
    }
  }

  return EndpointPostApi
}

/**
 * A QUERY client: can have a body like POST, but idempotent like GET. Same as {@link PostApi}
 * but for the QUERY verb.
 */
export const QueryApi = <
  TEndpointType extends TAnyEndpoint & {request?: IJSONObjectConvertible},
  TResult = TResponseOf<TEndpointType>,
  TPathPieces extends IApiPathPieces = TPathPiecesOf<TEndpointType>,
  TUnprocessedRequest = TRequestOf<TEndpointType>,
>() => {
  abstract class EndpointQueryApi extends PostApi<
    TEndpointType,
    TResult,
    TPathPieces,
    TUnprocessedRequest
  >() {
    protected getHTTPVerb(): THTTPVerb {
      return 'QUERY'
    }
  }

  return EndpointQueryApi
}

/**
 * The GET counterpart to {@link PostApi}; see there for how the endpoint is threaded.
 *
 * Params are the contract request type unchanged; intersecting with {@link TGetQueryParameters}
 * would let its index signature accept any undeclared key.
 *
 * The `request?:` clause of the `TEndpointType` constraint below is what enforces that, and
 * it can't be left to `GetApiBase`'s own `extends TGetQueryParameters`: while `TEndpointType`
 * is unresolved TypeScript defers that check and never revisits it once a concrete endpoint
 * is supplied. A `{filter: {deep: string}}` endpoint would then compile and serialize to
 * `filter=%5Bobject+Object%5D`.
 */
export const GetApi = <
  TEndpointType extends TAnyEndpoint & {request?: TGetQueryParameters},
  TResult = TResponseOf<TEndpointType>,
  // Not intersected with `IApiPathPieces`; see `PostApi` above for why.
  TPathPieces extends IApiPathPieces = TPathPiecesOf<TEndpointType>,
>() => {
  abstract class EndpointGetApi extends GetApiBase<
    TRequestOf<TEndpointType>,
    TResponseOf<TEndpointType> & IJSONObject,
    TPathPieces,
    TResult
  > {
    constructor(protected readonly endpoint: TEndpointType) {
      super()
    }

    protected getPath(pathPieces: TPathPieces): string {
      return this.endpoint.path(pathPieces as TPathPiecesOf<TEndpointType>)
    }
  }

  return EndpointGetApi
}
